import { useEffect, useId, useRef, useState } from 'react';
import { api, backupDownloadUrl, type RestoreReport } from '../api';
import { Icon, type IconName } from '../components/icons';
import { Modal } from '../components/Modal';
import { Btn, Card, DataGate, Dot, PageHead } from '../components/ui';
import { rawIntegrityView } from '../raw-integrity';
import { useApp } from '../state';
import { useResource } from '../useResource';

type Toast = (m: string, t?: 'ok' | 'warn' | 'err' | 'info') => void;

// Maintenance (BRIEF §5.13, edi.md §15) : sauvegarder et restaurer la console SANS ouvrir un SSH — la
// reprise après incident ne doit pas dépendre de la présence d'un administrateur système.
// ⚠️ Rien n'est simulé (§23.0) : l'état vient des deux vrais magasins (PostgreSQL + stockage d'objets), et le ZIP est
// téléchargé par le navigateur, dont la barre de progression est la VRAIE (jamais un chronomètre).
// ⚠️ L'archive contient TOUT, secrets compris (décision 2026-08-17) — cet écran disait l'inverse et c'est
// justement lui qui sert à faire circuler le ZIP. Il doit donc énoncer les deux vérités : la sauvegarde
// permet une reprise COMPLÈTE, et c'est un artefact SENSIBLE. Les identifiants de transport y sont chiffrés
// par `SETTINGS_KEY`, qui n'est PAS dans l'archive : à sauvegarder à part, sinon ils sont irrécupérables.
// Le rapport de restauration affiche le VERDICT de clé renvoyé par le serveur (jamais une phrase en dur).
export function Maintenance() {
  const { showToast, guard, admin } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [report, setReport] = useState<RestoreReport | null>(null);

  // ⚠️ AUCUN `isEmpty` : un statut de magasins n'est jamais « vide » — il est lu ou il ne l'est pas. Un
  // magasin en panne se dit ICI (pastille « indisponible » + message d'erreur), pas en effaçant l'écran.
  const { state, data: status, reload, error } = useResource((signal) => api.maintenanceStatus(signal));

  const startBackup = () => {
    if (!guard()) return;
    // Lien direct : le navigateur télécharge le ZIP (Content-Disposition) et affiche SA progression réelle.
    const a = document.createElement('a');
    a.href = backupDownloadUrl();
    a.download = '';
    document.body.appendChild(a);
    a.click();
    a.remove();
    showToast('Sauvegarde en cours de téléchargement…', 'info');
  };

  const c = status?.counts;
  // ⚠️ TITRE DE PAGE AJOUTÉ (relevé de la maquette) : cet écran démarrait sur sa première section,
  // sans dire où l'on est. Le fil d'Ariane de la barre du haut le nomme, mais il ne porte pas la
  // promesse — « sans SSH » est ce qui distingue cet écran d'une procédure serveur.
  const pageHead = (
    <PageHead title="Maintenance" sub="Sauvegarder et restaurer sans SSH — un ZIP suffit à relever l'EDI sur un autre serveur" />
  );

  return (
    <DataGate state={state} onRetry={reload} error={error} head={pageHead}>
      {/* État des deux magasins */}
      <SectionTitle title="État des magasins" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StoreCard icon="layers" name="Base PostgreSQL" ok={status?.db.ok ?? false} error={status?.db.error}
          lines={c ? [`${c.messages} message(s)`, `${c.suppliers} fournisseur(s) · ${c.sites} site(s)`, `${c.mappings} mapping(s) · ${c.transports} transport(s)`] : []} />
        <StoreCard icon="save" name="Stockage d’objets (Versity)" ok={status?.storage.ok ?? false} error={status?.storage.error}
          {...(status ? rawIntegrityView(status) : { lines: [], alert: null, missingKeys: [] })} />
      </div>

      {/* Sauvegarde */}
      <SectionTitle title="Sauvegarde" />
      <Card style={{ padding: 18, marginBottom: 20 }}>
        <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.5, marginBottom: 14 }}>
          Un <strong>ZIP horodaté</strong> (<code style={{ fontFamily: 'var(--font-mono)' }}>edi-backup-AAAAMMJJ-HHMMSS.zip</code>) qui embarque les <strong>deux magasins</strong> :
          l'export de la base (fournisseurs, transports, mappings, liens, planifs, rétention, journal, comptes, audit) <strong>et</strong> les fichiers bruts retenus.
          Le navigateur affiche la vraie progression et la taille finale.
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--amber-bg)', color: 'var(--ink-700)', borderRadius: 'var(--r-sm)', padding: '10px 12px', fontSize: 'var(--t-note)', lineHeight: 1.45, marginBottom: 14 }}>
          <span style={{ marginTop: 1 }}><Icon name="lock" size={14} /></span>
          <span>
            <strong>L'archive contient tout</strong> — comptes et identifiants compris : c'est ce qui permet une restauration <strong>réellement</strong> exploitable.
            Les identifiants de transport y sont <strong>chiffrés</strong> et n'ont de valeur qu'avec la <strong>même clé <code style={{ fontFamily: 'var(--font-mono)' }}>SETTINGS_KEY</code></strong>, qui n'est <strong>pas</strong> dans le ZIP : <strong>sauvegardez-la à part</strong>, sinon ils seront irrécupérables.
            <br />
            Traitez ce fichier comme la base elle-même : <strong>artefact sensible</strong> (empreintes de mots de passe, URL de webhook d'alerte en clair) → accès restreint, stockage chiffré, jamais en pièce jointe.
          </span>
        </div>
        <Btn icon="download" disabled={!admin} onClick={startBackup}>Créer et télécharger la sauvegarde</Btn>
        {!admin && <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', marginTop: 8 }}>Réservé aux administrateurs.</div>}
      </Card>

      {/* Restauration — réelle : upload ZIP + confirmation explicite + rapport RÉEL */}
      <SectionTitle title="Restauration" />
      <Card style={{ padding: 18 }}>
        <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.5, marginBottom: 14 }}>
          Déposez un ZIP de sauvegarde EDI : la restauration <strong>écrase les données</strong> (config + journal), <strong>remplace les comptes</strong> par ceux du ZIP et re-dépose les bruts.
          Les identifiants de transport reviennent <strong>chiffrés</strong> : ils ne fonctionneront que si cette instance a la <strong>même <code style={{ fontFamily: 'var(--font-mono)' }}>SETTINGS_KEY</code></strong> que la sauvegarde — le rapport le vérifie et le dit.
          Le <strong>journal d'audit</strong> n'est <strong>pas</strong> restauré (l'écraser effacerait qui a fait quoi depuis, y compris cette restauration). À faire de préférence pendant une fenêtre calme.
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" aria-label="Archive ZIP de sauvegarde" accept=".zip,application/zip" disabled={!admin}
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setReport(null); }}
            style={{ fontSize: 'var(--t-body)', color: 'var(--ink-700)' }} />
          <Btn variant="danger" icon="upload" disabled={!admin || !file} onClick={() => { if (guard()) setConfirm(true); }}>Restaurer…</Btn>
        </div>
        {!admin && <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', marginTop: 8 }}>Réservé aux administrateurs.</div>}
        {report && <RestoreResult report={report} />}
      </Card>

      {confirm && file && <RestoreModal file={file} onClose={() => setConfirm(false)} onDone={(r) => { setConfirm(false); setReport(r); void reload(); }} showToast={showToast} />}
    </DataGate>
  );
}

// Rapport de restauration (compteurs réels + fichiers en échec listés — jamais un « ✓ » optimiste §23.0).
// ⚠️ Une restauration dont les secrets ne sont pas déchiffrables ICI n'est pas un plein succès : elle sort en
// « réserve » (bandeau ambre), même sans aucun fichier en échec.
function RestoreResult({ report }: { report: RestoreReport }) {
  const total = Object.values(report.tables).reduce((a, b) => a + b, 0);
  const missing = report.missingInArchive;
  const ignored = report.ignoredTables ?? [];
  const hasErr = report.fileErrors.length > 0 || missing.length > 0 || ignored.length > 0 || report.secrets.keyMatch !== 'same-key';
  return (
    <div style={{ marginTop: 14, background: hasErr ? 'var(--amber-bg)' : 'var(--ok-bg)', color: hasErr ? 'var(--ink-700)' : 'var(--ok-fg)', borderRadius: 'var(--r-sm)', padding: '12px 14px', fontSize: 'var(--t-body)', lineHeight: 1.5 }}>
      <div style={{ fontWeight: 700 }}>Restauration terminée</div>
      <div style={{ marginTop: 4 }}>{total} ligne(s) restaurée(s) sur {Object.keys(report.tables).length} table(s) · {report.files} fichier(s) brut(s).</div>
      {(report.skippedTables?.length ?? 0) > 0 && (
        <div style={{ marginTop: 6 }}>
          ℹ️ Table(s) absente(s) du ZIP (sauvegarde antérieure), laissées intactes : <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)' }}>{report.skippedTables.join(', ')}</span>
        </div>
      )}
      {report.legacyMappingsConverted !== null && report.legacyMappingsConverted !== undefined && (
        <div style={{ marginTop: 6 }}>
          ℹ️ Sauvegarde antérieure aux mappings partagés : {report.legacyMappingsConverted} mapping(s) converti(s), rattaché(s) à leurs fournisseurs.
        </div>
      )}
      {/* Une table que cette version ne sait pas restaurer : ses lignes sont restées dans l'archive. Le taire
          laisserait croire à une reprise complète. */}
      {ignored.length > 0 && (
        <div style={{ marginTop: 6 }}>
          ⚠️ Table(s) de la sauvegarde que cette version ne restaure pas — leurs données ne sont PAS revenues : <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)' }}>{ignored.join(', ')}</span>
        </div>
      )}
      {report.fileErrors.length > 0 && (
        <div style={{ marginTop: 6 }}>
          ⚠️ {report.fileErrors.length} fichier(s) en échec :
          <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)' }}>
            {report.fileErrors.slice(0, 6).map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
      {/* Bruts déjà absents de la sauvegarde : leurs messages reviennent sans fichier — à dire avant le premier
          rejeu qui échoue, pas après. */}
      {missing.length > 0 && (
        <div style={{ marginTop: 6 }}>
          ⚠️ {missing.length} fichier(s) brut(s) manquaient déjà à la sauvegarde — leurs messages sont restaurés, sans rejeu ni téléchargement possibles :
          <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)' }}>
            {missing.slice(0, 6).map((k) => <li key={k}>{k}</li>)}
          </ul>
        </div>
      )}
      {/* Verdict de clé — texte RENVOYÉ PAR LE SERVEUR (il seul sait comparer les empreintes) : une clé
          différente laisse des transports inertes, ce qui ne doit jamais rester silencieux (§23.0). */}
      <div style={{ marginTop: 6, fontSize: 'var(--t-meta)', color: report.secrets.keyMatch === 'same-key' ? 'inherit' : 'var(--ink-700)' }}>
        {report.secrets.keyMatch === 'same-key' ? '🔑 ' : '⚠️ '}{report.secrets.detail}
      </div>
    </div>
  );
}

// Modale de confirmation — écriture destructive : exige de taper « RESTAURER ». Rien n'est simulé (§23.0).
function RestoreModal({ file, onClose, onDone, showToast }: { file: File; onClose: () => void; onDone: (r: RestoreReport) => void; showToast: Toast }) {
  /** Le préfixe des identifiants qui relient chaque libellé à son champ. */
  const fieldId = useId();
  const { guard } = useApp(); // défense en profondeur : l'action destructive revérifie la capacité d'écriture
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const phraseRef = useRef<HTMLInputElement>(null);
  // ⚠️ Le champ de recopie prend le focus APRÈS la modale (l'effet d'un parent passe après celui de l'enfant) : un
  //    `autoFocus` serait pris pour le déclencheur, et le focus ne reviendrait plus au bouton à la fermeture.
  useEffect(() => { phraseRef.current?.focus(); }, []);
  const ok = phrase.trim().toUpperCase() === 'RESTAURER';
  const run = async () => {
    if (!guard() || !ok) return;
    setBusy(true);
    try {
      const r = await api.restoreBackup(file);
      // ⚠️ « Terminée » n'est pas « sans réserve » : un fichier en échec OU une clé qui ne déchiffre pas les
      // secrets restaurés sort en `warn`, le détail restant lisible dans le rapport.
      showToast('Restauration terminée', r.fileErrors.length || r.missingInArchive.length || r.ignoredTables.length || r.secrets.keyMatch !== 'same-key' ? 'warn' : 'ok');
      onDone(r);
    } catch (e) {
      showToast('Restauration échouée : ' + (e as Error).message, 'err');
      setBusy(false);
    }
  };
  return (
    <Modal icon="alert" title="Restaurer et écraser les données ?" sub={file.name} onClose={busy ? () => undefined : onClose}
      footer={<><Btn variant="ghost" mutating={false} disabled={busy} onClick={onClose}>Annuler</Btn><Btn variant="danger" disabled={!ok || busy} onClick={() => void run()}>{busy ? 'Restauration…' : 'Restaurer'}</Btn></>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ background: 'var(--rose-bg)', color: 'var(--rose-fg)', borderRadius: 'var(--r-sm)', padding: '10px 12px', fontSize: 'var(--t-body)', lineHeight: 1.5 }}>
          ⚠️ Les données actuelles (fournisseurs, mappings, liens, journal) <strong>et les comptes</strong> seront <strong>remplacés</strong> par le contenu du ZIP. Action <strong>irréversible</strong>.
          Un compte créé après la sauvegarde <strong>disparaîtra</strong> — y compris le vôtre, ce qui vous déconnecterait. Le journal d'audit, lui, est conservé.
        </div>
        <div>
          <label htmlFor={`${fieldId}-0`} style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)', display: 'block', marginBottom: 5 }}>Tapez <strong>RESTAURER</strong> pour confirmer</label>
          <input id={`${fieldId}-0`} ref={phraseRef} value={phrase} onChange={(e) => setPhrase(e.target.value)} disabled={busy} placeholder="RESTAURER"
            style={{ width: '100%', height: 38, padding: '0 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', fontFamily: 'var(--font-mono)' }}
            onKeyDown={(e) => e.key === 'Enter' && void run()} />
        </div>
        {busy && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>Restauration en cours (ne fermez pas la fenêtre)…</div>}
      </div>
    </Modal>
  );
}

function SectionTitle({ title }: { title: string }) {
  return <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-800)', margin: '2px 2px 10px' }}>{title}</div>;
}

// ⚠️ `alert` est distinct d'`error` : un magasin peut répondre (« opérationnel ») et porter quand même un défaut de
// CONTENU — des fichiers bruts que la base référence et qu'il n'a plus. Le confondre avec la panne ferait dire
// « indisponible » d'un stockage qui fonctionne ; le taire ferait dire « tout va bien » d'un stockage vidé.
function StoreCard({ icon, name, ok, error, lines, alert, missingKeys }: { icon: IconName; name: string; ok: boolean; error?: string; lines: string[]; alert?: string | null; missingKeys?: string[] }) {
  return (
    <Card style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name={icon} size={17} /></div>
        <div style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{name}</div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-note)', color: ok ? 'var(--ok-fg)' : 'var(--rose-fg)' }}><Dot kind={ok ? 'ok' : 'err'} />{ok ? 'opérationnel' : 'indisponible'}</span>
      </div>
      {error && <div style={{ fontSize: 'var(--t-note)', color: 'var(--rose-fg)', wordBreak: 'break-word', marginBottom: 6 }}>{error}</div>}
      {lines.map((l, i) => <div key={i} style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)', marginTop: 2 }}>{l}</div>)}
      {alert && (
        <div role="status" style={{ marginTop: 8, background: 'var(--amber-bg)', color: 'var(--ink-700)', borderRadius: 'var(--r-sm)', padding: '8px 10px', fontSize: 'var(--t-note)', lineHeight: 1.45, wordBreak: 'break-word' }}>
          ⚠️ {alert}
          {(missingKeys?.length ?? 0) > 0 && (
            <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)' }}>
              {missingKeys?.slice(0, 6).map((k) => <li key={k}>{k}</li>)}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
